import { useEffect, useMemo } from "react";
import { useAtomValue } from "jotai";
import {
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
} from "../store/atoms";
import { Flex } from "src/elements";
import { ItemDetails } from "./components/ItemDetails";
import { ContainerDetails } from "./components/ContainerDetails";
import { ShelfDetails } from "./components/ShelfDetails";
import { relaxedItemsAtom } from "src/components/Planogram/store/atoms";
import { RelaxedItemConstraint } from "@CommonTypes/merchflow/pog/pog";
import { useApi } from "src/api";
import { ResponseGetMerchantSettings } from "@CommonApi/merchant-settings";
import { useAsyncState } from "src/hooks/useAsyncState";
import {
  toPegboardPlacementView,
  toPlanogramItemView,
} from "src/components/Planogram/common/planogramItemView";

export const PlanogramHovererDetails = () => {
  const hoveredShelfItem = useAtomValue(planogramHoveredItemAtom);
  const hoveredPegboardPlacement = useAtomValue(planogramHoveredPegboardPlacementAtom);
  const hoveredContainer = useAtomValue(planogramHoveredContainerAtom);
  const hoveredShelf = useAtomValue(planogramHoveredShelfAtom);
  const relaxedItems = useAtomValue(relaxedItemsAtom);

  const { getMerchantSettingsApi } = useApi();
  const [merchantSettings, setMerchantSettings] = useAsyncState<ResponseGetMerchantSettings>();

  useEffect(() => {
    setMerchantSettings(getMerchantSettingsApi());
  }, []);

  const relaxedItemsMapping = useMemo(
    () =>
      relaxedItems?.reduce(
        (total, cur) => {
          total[cur.entity.itemId] = cur;
          return total;
        },
        {} as Record<string, RelaxedItemConstraint>,
      ),
    [relaxedItems],
  );

  const showMagnitude = Boolean(relaxedItems?.some((item) => item.magnitude !== "fully_relaxed"));
  const dosDisplayAsPercentage = Boolean(merchantSettings?.dosPercentage);
  const hoveredViewItem = hoveredShelfItem
    ? toPlanogramItemView(hoveredShelfItem)
    : hoveredPegboardPlacement
      ? toPegboardPlacementView(hoveredPegboardPlacement)
      : null;

  return (
    <Flex minHeight="24px" gap="5px" align="center" overflow="hidden">
      {hoveredViewItem && (
        <ItemDetails
          item={hoveredViewItem}
          relaxedItemsMapping={
            hoveredViewItem.kind === "SHELF_ITEM" ? relaxedItemsMapping : undefined
          }
          showMagnitude={hoveredViewItem.kind === "SHELF_ITEM" && showMagnitude}
          dosDisplayAsPercentage={dosDisplayAsPercentage}
        />
      )}

      {hoveredContainer && <ContainerDetails container={hoveredContainer} />}

      {hoveredShelf && (
        <ShelfDetails
          shelf={hoveredShelf}
          isShelfWeightEnabled={Boolean(merchantSettings?.shelfWeightEnabled)}
        />
      )}
    </Flex>
  );
};
